import { useEffect, useLayoutEffect, type RefObject } from 'react';
import { findAnchor, type TourSide } from '@/features/onboarding-tour';
import { placeCard } from './place-card';

const SPOT_PAD = 6;

const SPOT_EDGE = 4;
const HIT_INSET = 0.3;

const SPOT_VARS = [
  '--tour-spot-x',
  '--tour-spot-y',
  '--tour-spot-w',
  '--tour-spot-h',
  '--tour-spot-r',
  '--tour-spot-ring',
  '--tour-hit-x',
  '--tour-hit-y',
  '--tour-hit-w',
  '--tour-hit-h',
];

interface SpotlightOptions {
  popupRef: RefObject<HTMLDivElement>;
  side: TourSide;
  sheet: boolean;
  scroll: ScrollLogicalPosition;
}

export function useSpotlight(anchor: string | null, { popupRef, side, sheet, scroll }: SpotlightOptions) {
  useLayoutEffect(() => {
    if (anchor) findAnchor(anchor)?.scrollIntoView({ block: scroll, inline: 'nearest' });
  }, [anchor, scroll]);

  useEffect(() => {
    const root = document.documentElement;
    let frame = 0;
    const tick = () => {
      const el = anchor ? findAnchor(anchor) : null;
      const popup = popupRef.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (el) {
        const r = el.getBoundingClientRect();
        const left = Math.max(r.left - SPOT_PAD, SPOT_EDGE);
        const top = Math.max(r.top - SPOT_PAD, SPOT_EDGE);
        const box = {
          left,
          top,
          width: Math.min(r.right + SPOT_PAD, vw - SPOT_EDGE) - left,
          height: Math.min(r.bottom + SPOT_PAD, vh - SPOT_EDGE) - top,
        };
        const radius = Math.min(
          (parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0) + SPOT_PAD,
          box.width / 2,
          box.height / 2,
        );
        const inset = radius * HIT_INSET;
        root.style.setProperty('--tour-spot-x', `${box.left}px`);
        root.style.setProperty('--tour-spot-y', `${box.top}px`);
        root.style.setProperty('--tour-spot-w', `${box.width}px`);
        root.style.setProperty('--tour-spot-h', `${box.height}px`);
        root.style.setProperty('--tour-spot-r', `${radius}px`);
        root.style.setProperty('--tour-hit-x', `${box.left + inset}px`);
        root.style.setProperty('--tour-hit-y', `${box.top + inset}px`);
        root.style.setProperty('--tour-hit-w', `${box.width - inset * 2}px`);
        root.style.setProperty('--tour-hit-h', `${box.height - inset * 2}px`);
        root.style.removeProperty('--tour-spot-ring');
        if (popup && sheet) {
          popup.toggleAttribute('data-dock-top', box.top + box.height > vh - popup.offsetHeight);
        } else if (popup) {
          const at = placeCard(box, { width: popup.offsetWidth, height: popup.offsetHeight }, { width: vw, height: vh }, side);
          popup.style.top = `${at.top}px`;
          popup.style.left = `${at.left}px`;
        }
      } else {
        root.style.setProperty('--tour-spot-x', `${vw / 2}px`);
        root.style.setProperty('--tour-spot-y', `${vh / 2}px`);
        root.style.setProperty('--tour-spot-w', '0px');
        root.style.setProperty('--tour-spot-h', '0px');
        root.style.setProperty('--tour-spot-r', '0px');
        root.style.setProperty('--tour-spot-ring', 'transparent');
        popup?.removeAttribute('data-dock-top');
      }
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [anchor, side, sheet, popupRef]);

  useEffect(
    () => () => {
      for (const v of SPOT_VARS) document.documentElement.style.removeProperty(v);
    },
    [],
  );
}
